import { Box, Button, Grid, IconButton, Skeleton, Typography } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { ReactComponent as RestaurantReviewQrMarkIcon } from '../../static/images/svg/restaurant-review-qr-mark.svg';
import QRCode from 'qrcode';
import React from 'react';
import CommonInput from '../../component/form/common-input.component';
import { FormFieldCell } from '../../component/form/form-field-cell.component';
import { RestaurantStaticImageUpload } from '../../component/restaurant-static-image-upload.component';
import { IMAGE_UPLOAD_SPECS } from '../../utils/image-upload-spec';
import {
    parseRestaurantStaticBundled,
    withImageCacheBust,
} from '../../utils/restaurant-static-bundled';

import GlobalNotification from '../../component/common/snackbar';
import { RESTAURANT_CURRENCY } from '../../constants/restaurant-eur-currency';
import apiManage, { apiManage_rbp } from '../../request';
import _ from 'lodash';
import { Country, Restaurant } from '../../models';
import './scss/restaurant-basic-info.style.scss';
import setLoading from '../../component/linear-determinate';
import i18n from '../../i18n';
import { LoadGoogleMap } from './map/google-maps-provider';
import ButtonComponent from '../../component/button/button.component';
import { getRBPStringId } from '../main/main-page-states';
import { timezoneData } from '../../constants/timezone.config';
import SelectAutocompleteComponent from '../../component/form/select-input-autocomplete';
import type { GooglePlace } from './map/google-search';
import LSM from '../../modules/cache/tool-local-storage';
import { LOCAL_STORAGE_KEYS } from '../../modules/cache/local-storage.constant';
import BusinessHourPage from './business-hour.page';
import DialogComponent from '../../component/dialog/dialog.component';

/** Figma 1566:90351 餐厅 Logo 展示边长（px，1:1） */
const RESTAURANT_LOGO_AVATAR_SIZE = 160;

export default class RestaurantBasisInfoPage extends React.Component<Props, State> {

    public MAP_KEY: string = '';
    // 时区
    public globalTimezones: { label: string, value: string }[] = [];

    public state: Readonly<State> = {
        // 餐厅地址
        address: {},
        // 加载
        loading: true,
        // 餐厅信息
        restaurant: {},
        // 国家信息
        countryList: [],
        /** bundled-image-urls 返回的 QOP Logo 完整 URL */
        qopLogoUrl: '',
        randomPicId: 0,
        // 谷歌地图key
        mapsKey: '',
        // 餐厅时区
        selectedTimezone: '',
        // 谷歌加载
        isLoading: false,
        /** 餐厅评论二维码弹窗 */
        reviewQrDialogOpen: false,
        reviewQrDataUrl: '',
    }

    // *********************
    // Life Cycle Function
    // *********************

    constructor(props: Props) {
        super(props);

        this.onSave = _.debounce(this.onSave, 500, {
            leading: true,
            trailing: false
        }).bind(this) as any;
        this.updateMapKey = _.debounce(this.updateMapKey, 500).bind(this) as any;
    }

    async componentDidMount() {
        await this.init()
    }
    // *********************
    // Default Function
    // *********************

    /**
     * 初始化
     */
    async init() {
        setLoading(true);
        const addressApi = this.getRestaurantAddress();
        const restaurantApi = this.getRestaurantInfo();
        const countryListApi = this.getCountry();
        const selectedTimezoneApi = this.getTimezones();
        const getGoogleMapApiKeyApi = this.getGoogleMapApiKey();
        const qopLogoUrlApi = this.getQopLogoUrl();
        const [address, restaurant, countryList, selectedTimezone, mapsKey, qopLogoUrl] = await Promise.all(
            [addressApi, restaurantApi, countryListApi, selectedTimezoneApi, getGoogleMapApiKeyApi, qopLogoUrlApi])
        this.MAP_KEY = mapsKey;
        this.setState({
            address,
            restaurant,
            countryList,
            selectedTimezone,
            mapsKey,
            qopLogoUrl,
            loading: false,
        })
        setLoading(false);
    }
    async getGoogleMapApiKey() {
        const result = await apiManage_rbp.getGoogleMapApiKey();
        return result?.map_key || '';
    }

    /**
     * 从餐厅静态资源集合读取 QOP Logo 完整 URL。
     * @returns 后端 `qopLogo` 字段；接口异常或无图时返回空字符串
     */
    async getQopLogoUrl(): Promise<string> {
        const result = await apiManage.get_restaurant_static_bundled_image_urls(getRBPStringId());
        if (_.get(result, 'error')) {
            return '';
        }
        return parseRestaurantStaticBundled(result).qopLogoUrl;
    }
    /**
     * 获取时区
     */
    async getTimezones() {
        this.globalTimezones = timezoneData.map((e, i) => { return { value: i + '', label: `${e.name1}(${e.name2})`, zone_name: `${e.zone}` } })
        const currentTimezone = await apiManage_rbp.getCurrentTimezone();
        const raw = currentTimezone?.[0]?.value;
        /** 索引 `0` 为合法值；`|| ''` 会把 `0` 误判为空导致保存报「时区必填」 */
        if (raw === undefined || raw === null || raw === '') {
            return '0';
        }
        return String(raw);
    }

    /**
     * 获取地址信息
     */
    async getRestaurantAddress() {
        const result = await apiManage_rbp.getRestaurantAddress();
        if (!_.get(result, 'error')) {
            return ({
                google_map_lat: result.google_map_lat || 0,
                google_map_lng: result.google_map_lng || 0,
                map_adress: result.map_adress || '',
                place_id: result.place_id || (result as { google_map_place_id?: string }).google_map_place_id || '',
            })
        }
        return ({
            google_map_lat: 0,
            google_map_lng: 0,
            map_adress: '',
            place_id: '',
        })
    }

    /**
     * 获取餐厅信息
     */
    async getRestaurantInfo() {
        const result = await apiManage_rbp.get_restaurant_info();
        if (!_.get(result, 'error')) {
            return (result)
        }
        return {}
    }

    /**
     *  获取国家信息
     */
    async getCountry() {
        const result = await apiManage_rbp.get_country();

        if (!_.get(result, 'error')) {
            return (result)
        }
        return ([])
    }

    /**
     * 保存
     */
    async onSave() {
        const { restaurant, address, selectedTimezone } = this.state
        // 餐厅名称必填
        if (!_.trim(restaurant?.restaurant_name)) {
            GlobalNotification.error(i18n.t('restaurant_type_restaurant_name'));
            return;
        }
        if (address.map_adress && !address.google_map_lng && !address.google_map_lat) {
            return GlobalNotification.error(i18n.t('google_unvalid_address'));
        }
        if (restaurant.currency_name === '') {
            GlobalNotification.error(`${i18n.t('restaurant_currency')} ${i18n.t('input_rule_text_required')}`);
            return;
        }
        if (selectedTimezone === '') {
            GlobalNotification.error(`${i18n.t('setting_page_text_select_time_zone')} ${i18n.t('input_rule_text_required')}`);
            return;
        }
        const api1 = this.saveRestaurantAddress();
        const api2 = this.saveRestaurantInfo();
        const api3 = apiManage_rbp.verifyCurrentTimezone(parseInt(this.state.selectedTimezone, 10));
        const api4 = this.onSaveMapKey();
        const [result1, result2, result3, result4] = await Promise.all([api1, api2, api3, api4]);

        if (result1 && result2 && !result3?.error && result4) {
            GlobalNotification.success(i18n.t('google_text_public_text_save_successfully'));
        }
        else {
            GlobalNotification.error(i18n.t('global_save_text_failed'));
        }
    }
    // 保存谷歌密钥
    onSaveMapKey = async () => {
        const result = await apiManage_rbp.saveGoogleMapApiKey(this.state.mapsKey);
        if (!result?.error) {
            return true
        } else {
            return false
        }
    }
    // 保存修改餐厅地址和修改有效范围
    async saveRestaurantAddress() {
        const { address } = this.state;
        const result = await apiManage_rbp.saveRestaurantAddress({
            google_map_lng: Number(address.google_map_lng || 0),
            google_map_lat: Number(address.google_map_lat || 0),
            map_adress: address.map_adress || '',
            ...(address.place_id ? { place_id: address.place_id } : {}),
        });
        if (!_.get(result, 'error')) {
            return true
        }
        else {
            return false
        }
    }

    // 修改餐馆信息
    async saveRestaurantInfo() {
        const { restaurant, countryList } = this.state;
        if (restaurant.restaurant_id === -1) {
            return false
        }
        const countryParams = countryList.find((item) => item.code === restaurant.code)
        const result = await apiManage_rbp.patch_restaurant_info({
            ...restaurant,
            code: undefined,
            name: undefined,
            ccTLD: countryParams?.ccTLD,
            country_code: countryParams?.code,
            country_id: countryParams?.country_id,
            fiscal_year_date: undefined
        });
        if (!_.get(result, 'error')) {
            LSM.set({ path: LOCAL_STORAGE_KEYS.RBP_RESTAURANT_COUNTRY, value: countryParams?.name })
            return true
        }
        else {
            return false
        }
    }

    /**
     * 修改回调
     * @param value
     * @param key
     */
    onChange(value: string, key: string) {
        const { restaurant } = this.state;
        _.set(restaurant, key, value)
        this.setState({
            restaurant
        })
    }

    /**
     * Logo 上传成功后刷新展示缓存破坏参数。
     */
    onLogoCommitted = async () => {
        const qopLogoUrl = await this.getQopLogoUrl();
        if (!qopLogoUrl) {
            GlobalNotification.error(i18n.t('google_text_verify_failed'));
            return;
        }
        this.setState({
            qopLogoUrl,
            randomPicId: Date.now(),
        });
        GlobalNotification.success(i18n.t('google_text_update_successfully'));
    };

    /**
     * Logo 删除成功后清空本地展示。
     */
    onLogoRemoved = () => {
        this.setState({
            qopLogoUrl: '',
            randomPicId: 0,
        });
        GlobalNotification.success(i18n.t('global_text_operate_success'));
    };

    /**
     * 为 bundled 接口返回的 QOP Logo URL 追加缓存破坏参数。
     * @returns 可展示 URL；后端未返回 Logo 时为空字符串
     */
    getLogoDisplayUrl(): string {
        const { qopLogoUrl, randomPicId } = this.state;
        return withImageCacheBust(qopLogoUrl, randomPicId) ?? '';
    }

    onSwitchTimezone = async (value: string) => {
        // const { value = '' } = e.target
        const selectedTimezone = this.globalTimezones.find(e => e.value === value)?.value || '';
        this.setState({ selectedTimezone });
        // const verifyCurrentTimezone = await apiManage_rbp.verifyCurrentTimezone(parseInt(value));
        // if (verifyCurrentTimezone.changedRows > 0) {
        //     const selectedTimezone = this.globalTimezones.find(e => e.value === value)?.value
        //     selectedTimezone && this.setState({
        //         selectedTimezone: selectedTimezone
        //     })
        // } else {
        //     GlobalNotification.error(i18n.t('global_save_text_failed'));
        // }
    }

    updateMapKey(value) {
        this.MAP_KEY = value;
        this.setState({ isLoading: value ? true : false, mapsKey: value });
        console.log(value)
    }

    /** 关闭评论二维码弹窗 */
    closeReviewQrDialog = () => {
        this.setState({ reviewQrDialogOpen: false, reviewQrDataUrl: '' });
    };

    /** 下载弹窗中的评论二维码图片（data URL） */
    downloadReviewQrImage = () => {
        const { reviewQrDataUrl } = this.state;
        if (!reviewQrDataUrl) {
            return;
        }
        const link = document.createElement('a');
        link.download = i18n.t('restaurant_basic_info_review_qr_download_filename');
        link.href = reviewQrDataUrl;
        link.click();
    };

    /**
     * 打开餐厅评论二维码：校验 Google place_id、拉取授权并校验 AU1003（POS 收银端）
     */
    openRestaurantReviewQrDialog = async () => {
        const place_id = (this.state.address.place_id || '').trim();
        if (!place_id) {
            GlobalNotification.error(i18n.t('restaurant_basic_info_review_qr_no_place_id'));
            return;
        }
        setLoading(true);
        try {
            const data = await apiManage_rbp.getAuthorizationMessage();
            if (_.get(data, 'error')) {
                GlobalNotification.error(i18n.t('warrant_page_msg_failed_obtain_authorization_data'));
                return;
            }
            const authorizationMessage =
                _.get(data, 'warrant_data') ??
                _.get(data, 'data') ??
                [];
            const list: unknown[] = Array.isArray(authorizationMessage)
                ? authorizationMessage
                : (_.get(authorizationMessage, 'list', []) as unknown[]);
            const hasAu1003 = list.some((item) => _.get(item, 'code') === 'AU1003');
            if (!hasAu1003) {
                GlobalNotification.error(i18n.t('restaurant_basic_info_review_qr_need_au1003'));
                return;
            }
            const reviewUrl = `https://search.google.com/local/writereview?placeid=${encodeURIComponent(place_id)}`;
            const dataUrl = await QRCode.toDataURL(reviewUrl, { width: 400, margin: 2 });
            this.setState({ reviewQrDialogOpen: true, reviewQrDataUrl: dataUrl });
        } catch {
            GlobalNotification.error(i18n.t('global_save_text_failed'));
        } finally {
            setLoading(false);
        }
    };
    // *********************
    // View
    // *********************

    render() {
        const { address, loading, restaurant, countryList, mapsKey, selectedTimezone, isLoading, reviewQrDialogOpen, reviewQrDataUrl } = this.state;
        return (
            <div className='restaurant-basic-info'>
                {
                    this.MAP_KEY &&
                    <LoadGoogleMap
                        googleMapsApiKey={this.MAP_KEY}
                        onLoaded={() => {
                            this.setState({ isLoading: false });
                        }} />
                }
                <Grid container spacing={4} className='common-card-box'>
                    <Grid item xs={12}>
                        <Box
                            className='restaurant-basic-info__panel'
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                alignItems: { xs: 'stretch', md: 'flex-start' },
                                justifyContent: 'space-between',
                                gap: { xs: 2, md: 3 },
                            }}
                        >
                            {/** 左侧 Logo（Figma 160×160，1:1）+ 右侧表单 */}
                            <Box
                                sx={{
                                    flex: '1 1 auto',
                                    minWidth: 0,
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    alignItems: { xs: 'stretch', md: 'flex-start' },
                                    gap: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        flex: '0 0 auto',
                                        width: RESTAURANT_LOGO_AVATAR_SIZE,
                                        height: RESTAURANT_LOGO_AVATAR_SIZE,
                                    }}
                                >
                                    {loading ? (
                                        <Skeleton
                                            variant="rounded"
                                            width={RESTAURANT_LOGO_AVATAR_SIZE}
                                            height={RESTAURANT_LOGO_AVATAR_SIZE}
                                            sx={{ borderRadius: '8px' }}
                                        />
                                    ) : (
                                        <RestaurantStaticImageUpload
                                            stringId={getRBPStringId()}
                                            kind="qop-logo-image"
                                            imageRef={this.state.qopLogoUrl}
                                            displayUrl={this.getLogoDisplayUrl()}
                                            onCommittedFileName={async () => {
                                                await this.onLogoCommitted();
                                            }}
                                            onRemoved={this.onLogoRemoved}
                                            avatarSize={RESTAURANT_LOGO_AVATAR_SIZE}
                                            uploadSpec={IMAGE_UPLOAD_SPECS.square}
                                            sx={{
                                                width: RESTAURANT_LOGO_AVATAR_SIZE,
                                                height: RESTAURANT_LOGO_AVATAR_SIZE,
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                                    {loading ? (
                                        renderRestaurantBasicInfoFieldsSkeleton()
                                    ) : (
                                        <Grid container spacing={2} className="restaurant-basic-info__fields" alignItems="stretch">
                                            {/** 第一行：餐厅名称 | 国家和地区 */}
                                            <Grid item xs={12} md={8} className="basic-input">
                                                <FormFieldCell label={i18n.t('restaurant_name')}>
                                                    <CommonInput
                                                        inputType="INPUT"
                                                        hiddenLabel
                                                        fullWidth
                                                        size="small"
                                                        variant="outlined"
                                                        required
                                                        regularType="required"
                                                        value={restaurant.restaurant_name || ''}
                                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                                            this.onChange(event.target.value, 'restaurant_name')
                                                        }
                                                    />
                                                </FormFieldCell>
                                            </Grid>
                                            <Grid item xs={12} md={4} className="basic-input">
                                                <FormFieldCell label={i18n.t('restaurant_Country_and_Region')} select>
                                                    <CommonInput
                                                        inputType="FLAG_INPUT"
                                                        hiddenLabel
                                                        fullWidth
                                                        size="small"
                                                        items={(countryList || []).map((item) => ({
                                                            value: item.code || '',
                                                            label: item.name || '',
                                                        }))}
                                                        value={_.get(restaurant, 'code', '')}
                                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                                            this.onChange(event.target.value, 'code')
                                                        }
                                                    />
                                                </FormFieldCell>
                                            </Grid>
                                            {/** 第二行：餐厅地址 | 货币 | 评论二维码 */}
                                            <Grid item xs={12} md={6} className="basic-input">
                                                <FormFieldCell label={i18n.t('restaurant_address')}>
                                                    <CommonInput
                                                        key={Number(loading)}
                                                        inputType="GOOGLE_PLACE"
                                                        hiddenLabel
                                                        fullWidth
                                                        size="small"
                                                        value={address.map_adress || ''}
                                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                            const map_adress = event.target.value;
                                                            this.setState((prev) => ({
                                                                address: {
                                                                    ...prev.address,
                                                                    map_adress,
                                                                    /** 手动改地址后原 place_id 失效，避免误用旧二维码 */
                                                                    place_id: undefined,
                                                                },
                                                            }));
                                                        }}
                                                        handleChangeGooglePlace={(val: GooglePlace) => {
                                                            this.setState({
                                                                address: {
                                                                    map_adress: val.value || '',
                                                                    google_map_lat: val.lat || 0,
                                                                    google_map_lng: val.lng || 0,
                                                                    place_id: val.place_id || '',
                                                                },
                                                            });
                                                        }}
                                                    />
                                                </FormFieldCell>
                                            </Grid>
                                            <Grid item xs={12} md={3} className="basic-input">
                                                <FormFieldCell label={i18n.t('restaurant_currency')} select>
                                                    <SelectAutocompleteComponent
                                                        hiddenLabel
                                                        inputVal={_.get(restaurant, 'currency_name', '€')}
                                                        options={RESTAURANT_CURRENCY || []}
                                                        label={i18n.t('restaurant_currency')}
                                                        onChange={(value) => this.onChange(value, 'currency_name')}
                                                    />
                                                </FormFieldCell>
                                            </Grid>
                                            <Grid
                                                item
                                                xs={12}
                                                md={3}
                                                className="basic-input"
                                                sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'stretch' }}
                                            >
                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        minWidth: 0,
                                                        minHeight: 56,
                                                        justifyContent: 'center',
                                                        alignItems: 'flex-start',
                                                    }}
                                                >
                                                    <Button
                                                        type="button"
                                                        variant="outlined"
                                                        size="medium"
                                                        startIcon={
                                                            <RestaurantReviewQrMarkIcon
                                                                width={32}
                                                                height={32}
                                                                style={{ display: 'block' }}
                                                            />
                                                        }
                                                        onClick={this.openRestaurantReviewQrDialog}
                                                        sx={{
                                                            minHeight: 56,
                                                            width: '100%',
                                                            px: 2,
                                                            py: 0,
                                                            textTransform: 'none',
                                                            justifyContent: 'flex-start',
                                                            borderRadius: '8px',
                                                            color: 'var(--text-dark)',
                                                            '& .MuiButton-startIcon': {
                                                                color: 'var(--icon-neutral-500)',
                                                            },
                                                            backgroundColor: 'var(--icon-primary-200)',
                                                            '&:hover': {
                                                                backgroundColor: 'var(--icon-primary-200)',
                                                                color: 'var(--text-dark)',
                                                                '& .MuiButton-startIcon': {
                                                                    color: 'var(--icon-neutral-500)',
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <Typography variant="subtitle1" component="span">
                                                            {i18n.t('restaurant_basic_info_review_qr_button')}
                                                        </Typography>
                                                    </Button>
                                                </Box>
                                            </Grid>
                                            {/** 第三行保留：时区 | Google 地图 key */}
                                            <Grid item xs={12} md={6} className="basic-input">
                                                <FormFieldCell label={i18n.t('setting_page_text_select_time_zone')} select>
                                                    <SelectAutocompleteComponent
                                                        hiddenLabel
                                                        inputVal={selectedTimezone || '0'}
                                                        options={this.globalTimezones}
                                                        label={i18n.t('setting_page_text_select_time_zone')}
                                                        onChange={(value) => this.onSwitchTimezone(value)}
                                                    />
                                                </FormFieldCell>
                                            </Grid>
                                            <Grid item xs={12} md={6} className="basic-input">
                                                <FormFieldCell label={i18n.t('google_maps_api_key')}>
                                                    <CommonInput
                                                        inputType="INPUT"
                                                        hiddenLabel
                                                        fullWidth
                                                        size="small"
                                                        variant="outlined"
                                                        value={mapsKey || ''}
                                                        disabled={isLoading}
                                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                            this.setState({ mapsKey: event.target.value });
                                                            this.updateMapKey(event.target.value);
                                                        }}
                                                    />
                                                </FormFieldCell>
                                            </Grid>
                                        </Grid>
                                    )}
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    flex: '0 0 auto',
                                    display: 'flex',
                                    justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                    alignItems: 'flex-start',
                                    pt: { xs: 0, md: 0.5 },
                                }}
                            >
                                <ButtonComponent
                                    action="SAVE"
                                    handleClick={this.onSave}
                                    disabled={loading}
                                    space={false}
                                />
                            </Box>
                        </Box>
                    </Grid>

                </Grid>
                <DialogComponent
                    open={reviewQrDialogOpen}
                    onClose={() => {
                        this.closeReviewQrDialog();
                    }}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        elevation: 0,
                        sx: {
                            width: '100%',
                            margin: '24px auto',
                            boxSizing: 'border-box',
                        },
                    }}
                    sx={[
                        {
                            '& .dialog-footer': {
                                display: 'none',
                            },
                            '& .MuiDialogContent-root': {
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            },
                        },
                    ]}
                    dialogContentStyle={{
                        paddingTop: 16,
                        paddingBottom: 24,
                        paddingLeft: 24,
                        paddingRight: 24,
                    }}
                    defaultHeaderProps={{
                        title: i18n.t('restaurant_basic_info_review_qr_dialog_title'),
                        titleAlign: 'left',
                        onClose: () => {
                            this.closeReviewQrDialog();
                        },
                        rightNode:
                            reviewQrDataUrl ? (
                                <IconButton
                                    type="button"
                                    size="small"
                                    aria-label={i18n.t('restaurant_basic_info_review_qr_download_aria')}
                                    onClick={this.downloadReviewQrImage}
                                    sx={{
                                        bgcolor: 'var(--fill-neutral-100)',
                                        border: '1px solid var(--stroke-primary-300)',
                                        borderRadius: '8px',
                                        width: 40,
                                        height: 40,
                                        '&:hover': {
                                            bgcolor: 'var(--fill-neutral-200)',
                                        },
                                    }}
                                >
                                    <GetAppIcon sx={{ fontSize: 22, color: 'var(--fill-primary-300)' }} />
                                </IconButton>
                            ) : undefined,
                    }}
                >
                    {reviewQrDataUrl ? (
                        <Box
                            component="img"
                            src={reviewQrDataUrl}
                            alt=""
                            sx={{ maxWidth: 1, width: 400, height: 'auto', display: 'block' }}
                        />
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            —
                        </Typography>
                    )}
                </DialogComponent>
                <Box sx={{ mt: 4 }} className="restaurant-basic-info__business-hour">
                    <BusinessHourPage />
                </Box>
            </div>

        )
    }
}

// *********************
// Type
// *********************
type Props = {}
type State = {
    // 餐厅地址
    address: {
        google_map_lat?: number;
        google_map_lng?: number;
        map_adress?: string;
        /** Google Place ID，用于生成 Google 写评论链接二维码 */
        place_id?: string;
    };
    // 加载
    loading: boolean;
    // 餐厅信息
    restaurant: Restaurant;
    // 国家信息
    countryList: Country[];
    /** bundled-image-urls 返回的 QOP Logo 完整 URL */
    qopLogoUrl: string;
    randomPicId: number;
    mapsKey: string;
    selectedTimezone: string;
    isLoading: boolean;
    reviewQrDialogOpen: boolean;
    reviewQrDataUrl: string;
}

/** 餐厅基础信息卡片字段区骨架（与 `FormFieldCell` 两列栅格布局一致） */
function renderRestaurantBasicInfoFieldsSkeleton(): JSX.Element {
    const fieldRow = (key: string): JSX.Element => (
        <Grid key={key} item xs={12} md={6} className="basic-input">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Skeleton variant="rounded" height={40} sx={{ flex: 1, borderRadius: '8px' }} />
            </Box>
        </Grid>
    );

    return (
        <Grid container spacing={2} className="restaurant-basic-info__fields" alignItems="stretch" aria-busy="true">
            {fieldRow('restaurant-basic-info-skeleton-name')}
            {fieldRow('restaurant-basic-info-skeleton-country')}
            {fieldRow('restaurant-basic-info-skeleton-currency')}
            {fieldRow('restaurant-basic-info-skeleton-timezone')}
            {fieldRow('restaurant-basic-info-skeleton-address')}
            {fieldRow('restaurant-basic-info-skeleton-map-key')}
            {fieldRow('restaurant-basic-info-skeleton-review-qr')}
        </Grid>
    );
}